import React, { useEffect, useState } from 'react';
import { fetchProjects, fetchDocuments } from '../services/api';
import { getProjectName } from '@shared/targetHelpers';
import { Field, ErrorText, errorMessage } from './ui';

// Choose the documents an agent judges against - a technical manual, a
// specification, an employer's requirements.
//
// Browsing is project-by-project because that is how documents are stored and
// listed, but the CHOSEN set is not: a standard applies across projects, and an
// agent is tenant-level, so selections survive switching project here and are
// shown together below the browser. Without that, picking a manual from one
// project and a spec from another would be impossible to see.
//
// blobName is the identity that matters - it is what the search index files a
// document and every chunk of it under, so it is what scopes retrieval at run
// time. fileName is carried alongside purely so a citation can name the
// standard without a second lookup.
export default function ReferenceDocumentPicker({ value = [], onChange }) {
  const selected = Array.isArray(value) ? value : [];

  const [projects, setProjects] = useState([]);
  const [projectName, setProjectName] = useState('');
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    fetchProjects()
      .then((list) => { if (mounted) setProjects(Array.isArray(list) ? list : []); })
      .catch((err) => { if (mounted) setError(errorMessage(err)); });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!projectName) { setDocuments([]); return undefined; }
    let mounted = true;
    setLoading(true);
    setError('');
    fetchDocuments(projectName)
      .then((docs) => { if (mounted) setDocuments(Array.isArray(docs) ? docs : []); })
      .catch((err) => { if (mounted) setError(errorMessage(err)); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [projectName]);

  function toggle(doc) {
    const blobName = doc?.blobName;
    if (!blobName) return;
    const already = selected.some((x) => x.blobName === blobName);
    onChange(already
      ? selected.filter((x) => x.blobName !== blobName)
      : [...selected, { blobName, fileName: doc.fileName || '', projectName }]);
  }

  // A document with no blobName cannot be retrieved against, so it is not
  // offered rather than being offered and silently doing nothing.
  const selectable = documents.filter((d) => d?.blobName);

  return (
    <Field
      label="Reference standards"
      hint="Documents this agent judges against. It retrieves only the passages that apply, and says so when none do - it never falls back on general knowledge."
    >
      <ErrorText>{error}</ErrorText>

      {selected.length > 0 && (
        <div className="pill-wrap" style={{ marginBottom: 10 }}>
          {selected.map((doc) => (
            <button
              key={doc.blobName}
              type="button"
              className="pill active"
              title={`${doc.fileName || doc.blobName}${doc.projectName ? ` — ${doc.projectName}` : ''} (click to remove)`}
              onClick={() => onChange(selected.filter((x) => x.blobName !== doc.blobName))}
            >
              {doc.fileName || doc.blobName} ×
            </button>
          ))}
        </div>
      )}

      <select className="input" value={projectName} onChange={(e) => setProjectName(e.target.value)}>
        <option value="">Choose a project to browse its documents…</option>
        {projects.map((p) => {
          const name = getProjectName(p);
          return name ? <option key={name} value={name}>{name}</option> : null;
        })}
      </select>

      {loading && <p className="sub" style={{ marginTop: 8 }}>Loading documents…</p>}

      {!loading && projectName && !selectable.length && (
        <p className="sub" style={{ marginTop: 8 }}>No documents in this project yet.</p>
      )}

      {!loading && selectable.length > 0 && (
        <div style={{ maxHeight: 260, overflowY: 'auto', marginTop: 8 }}>
          {selectable.map((doc) => {
            const active = selected.some((x) => x.blobName === doc.blobName);
            return (
              <label className="check-row" key={doc.blobName}>
                <input type="checkbox" checked={active} onChange={() => toggle(doc)} />
                <span>{doc.fileName || doc.blobName}</span>
              </label>
            );
          })}
        </div>
      )}

      {selected.length === 0 && (
        <p className="sub" style={{ marginTop: 8 }}>
          None chosen — the agent works as normal, with no standards to judge against.
        </p>
      )}
    </Field>
  );
}
